import { useContext, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../context/auth-context';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import WelcomeImage from '../assets/WelcomeImage.png';
import Sidebar from '../components/Sidebar';
import { getVacationRequests } from '../api/services';

const ManagerAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [allRequests, setAllRequests] = useState([]);

  // Update rows per page on screen resize
  useEffect(() => {
    setRowsPerPage(isMobile ? 5 : 10);
  }, [isMobile]);

  // Fetch dashboard data
  const fetchDashboardData = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const response = await getVacationRequests('Pending', 0, 1000);
      
      let requests = [];
      let pendingCount = 0;
      
      if (response && response.data && Array.isArray(response.data)) {
        requests = response.data;
        pendingCount = requests.filter((req) => req.status === 'PENDING').length;
      } else if (Array.isArray(response)) {
        requests = response;
        pendingCount = requests.filter((req) => req.status === 'PENDING').length;
      }

      if (pendingCount === 0) {
        const allResponse = await getVacationRequests('All', 0, 1000);
        let allRequestsData = [];
        
        if (allResponse && allResponse.data && Array.isArray(allResponse.data)) {
          allRequestsData = allResponse.data;
        } else if (Array.isArray(allResponse)) {
          allRequestsData = allResponse;
        }
        
        pendingCount = allRequestsData.filter((req) => req.status === 'PENDING').length;
        setAllRequests(allRequestsData);
        
        const totalRequests = allRequestsData.length;
        const approvedRequests = allRequestsData.filter((req) => req.status === 'APPROVED').length;
        const rejectedRequests = allRequestsData.filter((req) => req.status === 'REJECTED').length;
        
        setDashboardData({
          pendingRequests: pendingCount,
          totalRequests: totalRequests,
          approvedRequests: approvedRequests,
          rejectedRequests: rejectedRequests,
        });
      } else {
        setAllRequests(requests);
        
        const totalRequests = requests.length;
        const approvedRequests = requests.filter((req) => req.status === 'APPROVED').length;
        const rejectedRequests = requests.filter((req) => req.status === 'REJECTED').length;
        
        setDashboardData({
          pendingRequests: pendingCount,
          totalRequests: totalRequests,
          approvedRequests: approvedRequests,
          rejectedRequests: rejectedRequests,
        });
      }

      if (showRefresh) {
        setSnackbarMessage('Dashboard refreshed successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage(
        error.response?.status === 404
          ? 'No vacation requests found.'
          : error.message || 'Error fetching dashboard data'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDashboardData({
        pendingRequests: 0,
        totalRequests: 0,
        approvedRequests: 0,
        rejectedRequests: 0,
      });
      setAllRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const quickActions = [
    { text: 'Validate Metrics', path: '/validate-metrics', icon: TrendingUpIcon },
    { text: 'View Metrics', path: '/view-metrics', icon: DashboardIcon },
  ];

  // Get current page data for table
  const paginatedRequests = allRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Stats cards data
  const statsCards = [
    {
      title: 'Pending Requests',
      value: dashboardData?.pendingRequests || 0,
      icon: EventNoteIcon,
      color: '#ed6c02',
      bgColor: '#fff3e0',
    },
    {
      title: 'Total Requests',
      value: dashboardData?.totalRequests || 0,
      icon: DashboardIcon,
      color: '#009db9',
      bgColor: '#e0f7fa',
    },
    {
      title: 'Approved Requests',
      value: dashboardData?.approvedRequests || 0,
      icon: PeopleIcon,
      color: '#2e7d32',
      bgColor: '#e8f5e9',
    },
    {
      title: 'Rejected Requests',
      value: dashboardData?.rejectedRequests || 0,
      icon: TrendingUpIcon,
      color: '#d32f2f',
      bgColor: '#ffebee',
    },
  ];

  const getStatusChip = (status) => {
    const statusMap = {
      PENDING: { label: 'Pending', color: 'warning' },
      APPROVED: { label: 'Approved', color: 'success' },
      REJECTED: { label: 'Rejected', color: 'error' },
    };
    const config = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            pt: { xs: '10px', md: '120px' },
            bgcolor: '#f8fafc',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          {/* Mobile Menu Button */}
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'sticky',
              top: { xs: '64px', sm: '80px' },
              zIndex: 1100,
              mb: 2,
            }}
          >
            <Tooltip title="Menu" arrow>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  background: 'linear-gradient(135deg, #009db9 0%, #00707f 100%)',
                  color: '#fff',
                  borderRadius: '12px',
                  p: 1.2,
                  boxShadow: '0 4px 12px rgba(0,157,185,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00707f 0%, #005a66 100%)',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(0,157,185,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
                aria-label="toggle sidebar"
              >
                <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Welcome Card */}
          <Card
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #009db9 0%, #00707f 50%, #005a66 100%)',
              color: '#fff',
              borderRadius: '24px',
              px: { xs: 3, md: 5 },
              py: { xs: 4, md: 5 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 12px 40px rgba(0,157,185,0.3)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -100,
                right: -100,
                width: { xs: 200, sm: 250, md: 300 },
                height: { xs: 200, sm: 250, md: 300 },
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -150,
                left: -50,
                width: { xs: 250, sm: 300, md: 400 },
                height: { xs: 250, sm: 300, md: 400 },
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, flex: 1, width: '100%' }}>
              <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                Welcome back
              </Typography>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                mb: 1, 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
              }}>
                Hello, {user?.name || 'Manager'}!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Here's what's happening with your team today
              </Typography>
            </Box>
            <Box
              component="img"
              src={WelcomeImage}
              alt="Manager Illustration"
              sx={{
                width: { xs: '80%', sm: 150, md: 240 },
                maxWidth: '100%',
                height: 'auto',
                mt: { xs: 2, sm: 0 },
                ml: { sm: 2, md: 4 },
                objectFit: 'contain',
                position: 'relative',
                zIndex: 1,
                filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.2))',
              }}
            />
          </Card>

          {/* Quick Stats - Responsive Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {statsCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Grid item xs={6} md={3} key={index}>
                  <Card
                    sx={{
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" sx={{ 
                            color: '#64748b', 
                            fontWeight: 500,
                            fontSize: { xs: '0.65rem', sm: '0.75rem' }
                          }}>
                            {stat.title}
                          </Typography>
                          {loading ? (
                            <Box sx={{ mt: 1 }}>
                              <CircularProgress size={24} sx={{ color: '#009db9' }} />
                            </Box>
                          ) : (
                            <Typography variant="h4" sx={{ 
                              fontWeight: 700, 
                              color: '#0f172a', 
                              mt: 1,
                              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
                            }}>
                              {stat.value}
                            </Typography>
                          )}
                        </Box>
                        <Avatar
                          sx={{
                            bgcolor: stat.bgColor,
                            color: stat.color,
                            width: { xs: 40, sm: 48, md: 56 },
                            height: { xs: 40, sm: 48, md: 56 },
                            borderRadius: '16px',
                          }}
                        >
                          <Icon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' } }} />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Vacation Requests Table - Responsive */}
          <Card
            sx={{
              borderRadius: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    color: '#0f172a',
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}>
                    Recent Vacation Requests
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Manage and review team vacation requests
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={20} sx={{ color: '#009db9' }} /> : <RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      borderColor: '#e2e8f0',
                      color: '#475569',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      '&:hover': {
                        borderColor: '#009db9',
                        color: '#009db9',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/view-metrics"
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #009db9 0%, #00707f 100%)',
                      boxShadow: '0 4px 12px rgba(0,157,185,0.3)',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(0,157,185,0.4)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {isMobile ? 'View All' : 'View All Requests'}
                  </Button>
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#009db9' }} />
                  <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                    Loading vacation requests...
                  </Typography>
                </Box>
              ) : allRequests.length === 0 ? (
                <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: '#64748b' }}>
                    No vacation requests available
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: { xs: 600, sm: 700, md: 800 } }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Employee
                          </TableCell>
                          {!isMobile && (
                            <>
                              <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Start Date
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                End Date
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {isMobile ? 'Details' : 'Reason'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Status
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              Actions
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedRequests.map((request) => (
                          <TableRow
                            key={request.id}
                            sx={{
                              '&:hover': {
                                bgcolor: '#f8fafc',
                              },
                              transition: 'background-color 0.2s ease',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ 
                                  width: { xs: 28, sm: 32 }, 
                                  height: { xs: 28, sm: 32 }, 
                                  bgcolor: '#e0f7fa', 
                                  color: '#009db9' 
                                }}>
                                  {request.userName?.[0] || 'E'}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 500, 
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}>
                                    {request.userName || 'Unknown'}
                                  </Typography>
                                  {isMobile ? (
                                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                      {request.startDate ? new Date(request.startDate).toLocaleDateString() : 'N/A'} - {request.endDate ? new Date(request.endDate).toLocaleDateString() : 'N/A'}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                      ID: {request.userId || 'N/A'}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            {!isMobile && (
                              <>
                                <TableCell sx={{ color: '#0f172a', fontSize: '0.875rem' }}>
                                  {request.startDate ? new Date(request.startDate).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell sx={{ color: '#0f172a', fontSize: '0.875rem' }}>
                                  {request.endDate ? new Date(request.endDate).toLocaleDateString() : 'N/A'}
                                </TableCell>
                              </>
                            )}
                            <TableCell>
                              {isMobile ? (
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Reason: {request.reason || 'N/A'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {getStatusChip(request.status)}
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography variant="body2" sx={{ 
                                  maxWidth: 150, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.875rem'
                                }}>
                                  {request.reason || 'N/A'}
                                </Typography>
                              )}
                            </TableCell>
                            {!isMobile && (
                              <>
                                <TableCell>{getStatusChip(request.status)}</TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.5}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      sx={{ 
                                        borderRadius: '8px', 
                                        textTransform: 'none', 
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                        px: { xs: 1, sm: 1.5 }
                                      }}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="error"
                                      sx={{ 
                                        borderRadius: '8px', 
                                        textTransform: 'none', 
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                        px: { xs: 1, sm: 1.5 }
                                      }}
                                    >
                                      Reject
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={allRequests.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50]}
                    sx={{
                      borderTop: '1px solid #e2e8f0',
                      '.MuiTablePagination-select': {
                        borderRadius: '8px',
                      },
                      '.MuiTablePagination-selectLabel': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      },
                      '.MuiTablePagination-displayedRows': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      },
                      '.MuiTablePagination-actions': {
                        button: {
                          padding: { xs: '4px', sm: '8px' },
                        }
                      }
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions - Responsive */}
          <Card
            sx={{
              borderRadius: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              mt: 4,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: '#0f172a', 
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
              }}>
                Quick Actions
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  gap: 2,
                  justifyContent: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Button
                      key={action.text}
                      component={RouterLink}
                      to={action.path}
                      variant="contained"
                      startIcon={<Icon />}
                      fullWidth={isMobile}
                      sx={{
                        background: 'linear-gradient(135deg, #009db9 0%, #00707f 100%)',
                        borderRadius: '14px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        fontSize: { xs: '0.875rem', sm: '0.9rem', md: '0.95rem' },
                        boxShadow: '0 4px 12px rgba(0,157,185,0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0,157,185,0.4)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {action.text}
                    </Button>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: 260, sm: 280 },
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
            boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManagerAdminDashboard;